import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Check, Clock, BarChart3, Sparkles, ShoppingCart, Wrench, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { COURSE_DETAILS, COURSES_LIST } from "@/lib/data";
import { useCart, formatInr } from "@/lib/cart";
import { PRICING, usePricing } from "@/lib/pricing";
import Navigation from "@/components/site/Navigation";
import Footer from "@/components/site/Footer";
import CourseCurriculum from "@/components/site/CourseCurriculum";
import { mediaSrc } from "@/lib/media";

export default function CoursePage() {
  const { slug } = useParams();
  const nav = useNavigate();
  usePricing();
  const [live, setLive] = useState(null);
  const detail = live || COURSE_DETAILS[slug];
  const meta = COURSES_LIST.find((c) => c.slug === slug) || (live ? { ...live, icon: Sparkles } : null);
  const { has, toggleCourse, cart } = useCart();

  useEffect(() => {
    // Course content is admin-managed in the DB; static data.js is the offline fallback.
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/courses/${slug}`)
      .then(({ data }) =>
        setLive({
          ...data,
          curriculum: (data.modules || []).map((m) => ({
            title: m.title,
            sessions: m.sessions.map((s) => s.title),
          })),
          totalSessions: `${data.total_sessions} Sessions`,
        }),
      )
      .catch(() => {});
  }, [slug]);

  if (!detail || !meta) {
    return (
      <main className="min-h-screen bg-[#F8FAFC] dark:bg-[#020617] text-[#1E293B] dark:text-[#F8FAFC]">
        <Navigation />
        <div className="max-w-3xl mx-auto px-6 pt-40 pb-32 text-center">
          <h1 className="font-display text-4xl font-semibold">Course not found</h1>
          <button
            onClick={() => nav("/")}
            data-testid="course-back-home"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-6 py-3 text-sm"
          >
            <ArrowLeft className="h-4 w-4" /> Back home
          </button>
        </div>
        <Footer />
      </main>
    );
  }

  const Icon = meta.icon;

  return (
    <main
      data-testid="course-page"
      className="min-h-screen bg-[#F8FAFC] dark:bg-[#020617] text-[#1E293B] dark:text-[#F8FAFC]"
    >
      <Navigation />

      <section className="pt-32 pb-16 gradient-mesh">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Link
            to="/"
            data-testid="course-back-link"
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300 hover:text-[#0F172A] dark:hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" /> All courses
          </Link>

          {meta.cover && (
            <div
              data-testid="course-cover"
              className="mt-8 relative h-72 sm:h-96 w-full overflow-hidden rounded-3xl border border-slate-200/80 dark:border-white/10 hairline"
            >
              <img
                src={mediaSrc(meta.cover)}
                alt={detail.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#020617]/85 via-[#020617]/30 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur border border-white/20 px-2.5 py-1 text-[10px] uppercase tracking-[0.22em] text-white font-semibold">
                  <Icon className="h-3 w-3" /> Mastery Course
                </div>
                <h1
                  data-testid="course-title"
                  className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-semibold text-white text-balance"
                >
                  {detail.title}
                </h1>
              </div>
            </div>
          )}

          <div className="mt-10 grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <p className="text-lg text-slate-600 dark:text-slate-300 max-w-xl leading-relaxed">
                {detail.tagline}
              </p>
              <p className="mt-6 text-[15px] leading-relaxed text-slate-600 dark:text-slate-300 max-w-2xl">
                {detail.overview}
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3 text-xs">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-3 py-1.5 text-slate-600 dark:text-slate-300">
                  <Clock className="h-3 w-3" /> {detail.duration}
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-3 py-1.5 text-slate-600 dark:text-slate-300">
                  <BarChart3 className="h-3 w-3" /> {detail.level}
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[#06B6D4]/40 bg-[#06B6D4]/10 px-3 py-1.5 text-[#0F172A] dark:text-[#06B6D4]">
                  <Sparkles className="h-3 w-3" /> Included in Basic & Premium
                </span>
              </div>

              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => {
                    const added = has(slug);
                    if (!added && cart.n >= PRICING.maxCartCourses) {
                      toast.error(`Cart already has ${PRICING.maxCartCourses} courses (max).`);
                      return;
                    }
                    toggleCourse(slug);
                    if (!added) toast.success(`Added "${meta.title}" to cart`);
                    else toast(`Removed "${meta.title}"`);
                  }}
                  data-testid="course-add-to-cart"
                  className={`inline-flex justify-center items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold transition-all duration-300 ${
                    has(slug)
                      ? "bg-emerald-500 text-white hover:bg-emerald-600"
                      : "bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] hover:-translate-y-0.5"
                  }`}
                >
                  {has(slug) ? (
                    <>
                      <Check className="h-4 w-4" /> Added — Remove
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="h-4 w-4" /> Add to Cart · {formatInr(PRICING.coursePrice)}
                    </>
                  )}
                </button>
                <Link
                  to="/cart"
                  data-testid="course-cta-view-cart"
                  className="inline-flex justify-center items-center gap-2 rounded-full border border-slate-300 dark:border-white/15 px-6 py-3.5 text-sm font-medium text-[#0F172A] dark:text-white hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
                >
                  View Cart <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-3xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.04] p-8 hairline glass">
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0F172A] text-white dark:bg-white dark:text-[#0F172A]">
                  <Icon className="h-6 w-6" />
                </span>
                <h3 className="mt-6 font-display text-xl tracking-tight font-medium text-[#0F172A] dark:text-white">
                  Available inside
                </h3>
                <ul className="mt-4 space-y-3 text-sm">
                  <li className="flex items-center gap-3 text-slate-600 dark:text-slate-300">
                    <span className="inline-flex h-1.5 w-1.5 rounded-full bg-slate-400" />
                    E-Lite Package (any 2 courses)
                  </li>
                  <li className="flex items-center gap-3 text-slate-600 dark:text-slate-300">
                    <span className="inline-flex h-1.5 w-1.5 rounded-full bg-[#2563EB]" />
                    Basic Package (3 + 2 free)
                  </li>
                  <li className="flex items-center gap-3 text-[#0F172A] dark:text-white font-medium">
                    <span className="inline-flex h-1.5 w-1.5 rounded-full bg-[#06B6D4]" />
                    Premium Package (all {PRICING.totalCourses})
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-6">
          <div className="rounded-3xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.04] p-8 hairline">
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#2563EB] dark:text-[#06B6D4] font-semibold">
              Who it's for
            </span>
            <ul className="mt-5 space-y-3">
              {detail.forWho.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-[15px] text-slate-700 dark:text-slate-200">
                  <Check className="h-4 w-4 mt-0.5 text-[#06B6D4]" />
                  {p}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-3xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.04] p-8 hairline">
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#2563EB] dark:text-[#06B6D4] font-semibold">
              What you'll learn
            </span>
            <ul className="mt-5 space-y-3">
              {detail.youLearn.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-[15px] text-slate-700 dark:text-slate-200">
                  <Check className="h-4 w-4 mt-0.5 text-[#06B6D4]" />
                  {p}
                </li>
              ))}
            </ul>
          </div>

          {detail.tools && detail.tools.length > 0 && (
            <div
              data-testid="course-tools"
              className="lg:col-span-2 rounded-3xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.04] p-8 hairline"
            >
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#2563EB] dark:text-[#06B6D4] font-semibold">
                <Wrench className="h-3.5 w-3.5" /> Tools You&apos;ll Master
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                {detail.tools.map((t) => (
                  <span
                    key={t}
                    data-testid={`course-tool-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.03] px-3.5 py-2 text-sm font-medium text-slate-700 dark:text-slate-200"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}

          {detail.careers && detail.careers.length > 0 && (
            <div
              data-testid="course-careers"
              className="lg:col-span-2 rounded-3xl border border-slate-200/80 dark:border-white/10 bg-gradient-to-br from-slate-50 to-white dark:from-white/[0.05] dark:to-white/[0.02] p-8 hairline"
            >
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#2563EB] dark:text-[#06B6D4] font-semibold">
                <Briefcase className="h-3.5 w-3.5" /> Career Opportunities
              </div>
              <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {detail.careers.map((c) => (
                  <div
                    key={c}
                    data-testid={`course-career-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}`}
                    className="flex items-start gap-3 rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.04] p-4"
                  >
                    <span className="h-9 w-9 flex-none inline-flex items-center justify-center rounded-xl bg-[#06B6D4]/15 text-[#06B6D4]">
                      <Briefcase className="h-4 w-4" />
                    </span>
                    <span className="text-sm font-medium text-[#0F172A] dark:text-white">
                      {c}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {detail.curriculum && detail.curriculum.length > 0 && (
        <CourseCurriculum
          curriculum={detail.curriculum}
          totalSessions={detail.totalSessions}
          level={detail.level}
        />
      )}

      <Footer />
    </main>
  );
}
